// app/javascript/controllers/ui/manifesto/text_reveal_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Text Reveal Controller
 *
 * Раскрытие текста по словам при входе в viewport.
 * Каждое слово оборачивается в .mf-reveal > .mf-reveal__inner
 * с transform: translateY(105%) → 0, stagger по задержке.
 *
 * @usedby _hero.html.erb, _manifesto.html.erb
 *
 * Usage:
 *   <h1 data-controller="ui--manifesto--text-reveal"
 *       data-ui--manifesto--text-reveal-stagger-value="40">
 *     Прогресс из джунглей в машину
 *   </h1>
 *
 * CSS (уже в manifesto.css):
 *   .mf-reveal          { display: inline-block; overflow: hidden; }
 *   .mf-reveal__inner   { transform: translateY(105%); transition: transform .9s var(--mf-ease); }
 *   .mf-reveal.--in .mf-reveal__inner { transform: translateY(0); }
 */
export default class extends Controller {
  static values = {
    stagger:  { type: Number, default: 40 },
    threshold: { type: Number, default: 0.2 }
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.element.style.opacity = "1"
      return
    }

    this.#splitIntoWords()
    this.#observe()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  // Разбить текстовые узлы на слова, каждое — в обёртку
  #splitIntoWords() {
    this.wordEls = []

    // Рекурсивно обходим дочерние текстовые узлы.
    // Не трогаем inline-элементы (иконки, span'ы с классами) — рекурсируем внутрь.
    const walkAndSplit = (node) => {
      const children = Array.from(node.childNodes)
      for (const child of children) {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent
          if (!text.trim()) continue
          const frag = document.createDocumentFragment()
          const parts = text.split(/(\s+)/)
          for (const part of parts) {
            if (/^\s+$/.test(part)) {
              frag.appendChild(document.createTextNode(part))
            } else if (part.length > 0) {
              const wrap = document.createElement("span")
              wrap.className = "mf-reveal"
              const inner = document.createElement("span")
              inner.className = "mf-reveal__inner"
              inner.textContent = part
              wrap.appendChild(inner)
              frag.appendChild(wrap)
              this.wordEls.push(wrap)
            }
          }
          child.parentNode.replaceChild(frag, child)
        } else if (child.nodeType === Node.ELEMENT_NODE && !child.classList?.contains("mf-reveal")) {
          walkAndSplit(child)
        }
      }
    }

    walkAndSplit(this.element)
  }

  #observe() {
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#reveal()
          this.observer.disconnect()
          break
        }
      }
    }, { threshold: this.thresholdValue })
    this.observer.observe(this.element)
  }

  #reveal() {
    this.wordEls.forEach((el, i) => {
      setTimeout(() => el.classList.add("--in"), i * this.staggerValue)
    })
  }
}
